/**
 * Envelopes (toiyp Plus): the outside of a letter, drawn flat — a body and a
 * back flap with a fold line, filled with the book's pattern as a texture.
 * The pattern is busy by design, so the address sits on a plain cream label,
 * and the stamp (with its postmark) goes top right, tilted as on bubbles.
 *
 * `EnvelopeOpening` is the one moment of movement: the first time a delivered
 * letter is opened, the flap lifts and the paper slides out, then the letter
 * shows on its paper. With reduced motion it is skipped entirely.
 */

import { useEffect, useState, type ReactNode } from 'react';
import type { Stationery } from '../letters/api';

function patternStyle(envelope: Stationery): React.CSSProperties {
  return {
    '--env-image': `url(${envelope.imageUrl})`,
    '--env-size': `${Math.round(envelope.scale * 100)}% auto`,
  } as React.CSSProperties;
}

export function Envelope({
  envelope,
  to,
  from,
  stamp,
  compact = false,
  children,
}: {
  envelope: Stationery;
  /** "To @tom" — always the recipient's handle. */
  to: string;
  /** "from @leon"; omitted where the sender must not be named. */
  from?: string | null;
  stamp?: ReactNode;
  /** The thread's size: a little envelope, not a poster. */
  compact?: boolean;
  /** Extra lines on the label (e.g. "arrives tomorrow"). */
  children?: ReactNode;
}) {
  return (
    <div className={`envelope${compact ? ' envelope--compact' : ''}`} style={patternStyle(envelope)}>
      <span className="envelope__body" aria-hidden="true" />
      <span className="envelope__flap" aria-hidden="true" />
      <svg className="envelope__fold" viewBox="0 0 300 200" preserveAspectRatio="none" aria-hidden="true">
        <polyline points="0,0 150,96 300,0" />
      </svg>
      {stamp && <span className="envelope__stamp">{stamp}</span>}
      <span className="envelope__label">
        <span className="envelope__to">{to}</span>
        {from && <span className="envelope__from">{from}</span>}
        {children}
      </span>
    </div>
  );
}

/** A small envelope for beside the stamp and in the postal record. */
export function EnvelopeThumb({ envelope, title }: { envelope: Stationery; title?: string }) {
  return (
    <span
      className="envelope-thumb"
      style={patternStyle(envelope)}
      role="img"
      aria-label={title ?? `Came in the ${envelope.name} envelope`}
      title={title ?? `Came in the ${envelope.name} envelope`}
    >
      <span className="envelope-thumb__flap" aria-hidden="true" />
    </span>
  );
}

function reducedMotion(): boolean {
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

/** Whether the opening moment should play at all. */
export function shouldPlayOpening(): boolean {
  return !reducedMotion();
}

/**
 * The flap lifts, the paper slides out, the envelope fades: about a second
 * and a half, then `onDone`. Tapping skips it.
 */
export function EnvelopeOpening({
  envelope,
  to,
  from,
  stamp,
  onDone,
}: {
  envelope: Stationery;
  to: string;
  from?: string | null;
  stamp?: ReactNode;
  onDone: () => void;
}) {
  const [phase, setPhase] = useState<'closed' | 'open' | 'out'>('closed');
  useEffect(() => {
    const a = window.setTimeout(() => setPhase('open'), 250);
    const b = window.setTimeout(() => setPhase('out'), 1050);
    const c = window.setTimeout(onDone, 1700);
    return () => [a, b, c].forEach((t) => window.clearTimeout(t));
  }, [onDone]);

  return (
    <button
      type="button"
      className={`envelope-opening envelope-opening--${phase}`}
      onClick={onDone}
      aria-label="Opening the envelope — tap to skip"
    >
      <span className="envelope-opening__stage" style={patternStyle(envelope)}>
        <span className="envelope-opening__back" aria-hidden="true" />
        <span className="envelope-opening__paper" aria-hidden="true" />
        <span className="envelope-opening__pocket" aria-hidden="true" />
        <span className="envelope-opening__flap" aria-hidden="true" />
        {stamp && <span className="envelope__stamp">{stamp}</span>}
        <span className="envelope__label">
          <span className="envelope__to">{to}</span>
          {from && <span className="envelope__from">{from}</span>}
        </span>
      </span>
    </button>
  );
}
